/**
 * Simulação de taxas do Mercado Pago (link de pagamento, cartão de crédito).
 *
 * Duas taxas somadas:
 *  - taxa por venda (2,99%), sempre cobrada;
 *  - taxa de parcelamento, que depende do número de parcelas (zero à vista).
 *
 * Cálculo em centavos inteiros (ver calculo.ts). As taxas ficam em pontos-base
 * (1 ponto-base = 0,01%), também inteiros: 2,99% = 299.
 */
import { fromCents, toCents } from './calculo';

export interface TabelaTaxas {
  /** Taxa por venda em pontos-base (299 = 2,99%). */
  taxaVendaBp: number;
  /** Taxa de parcelamento por número de parcelas: índice 0 = 1x (à vista). */
  parcelamentoBp: number[];
  /** Data da última conferência com o Mercado Pago (AAAA-MM-DD). */
  conferidaEm: string;
}

/** Taxas do Mercado Pago conferidas em 02/11/2026 (link de pagamento, cartão de crédito). */
export const TAXAS_MERCADO_PAGO_PADRAO: TabelaTaxas = {
  taxaVendaBp: 299,
  parcelamentoBp: [0, 227, 285, 347, 406, 464, 478, 540, 602, 647],
  conferidaEm: '2026-11-02',
};

export const MAX_PARCELAS = 10;

export interface OpcaoParcelamento {
  parcelas: number;
  /** Taxa total aplicada (venda + parcelamento), em pontos-base. */
  taxaTotalBp: number;
  taxaParcelamentoBp: number;
  /** Quanto o cliente paga no total. */
  clientePaga: string;
  /** Valor de cada parcela para o cliente. */
  valorParcela: string;
  /** Quanto a agência recebe depois das taxas. */
  voceRecebe: string;
  descontoTaxaVenda: string;
  descontoParcelamento: string;
}

/** Arredonda a/b (inteiros positivos) meio para cima. */
function divRound(a: number, b: number): number {
  return Math.floor(a / b + 0.5);
}

/** Validação da tabela (vinda do banco ou do formulário do admin). */
export function validarTabela(t: TabelaTaxas): string | null {
  const ok = (bp: number) => Number.isInteger(bp) && bp >= 0 && bp < 5000;
  if (!ok(t.taxaVendaBp)) return 'Taxa por venda inválida.';
  if (t.parcelamentoBp.length !== MAX_PARCELAS) return `Informe as taxas de 1x a ${MAX_PARCELAS}x.`;
  if (!t.parcelamentoBp.every(ok)) return 'Taxa de parcelamento inválida.';
  if (t.parcelamentoBp[0] !== 0) return 'À vista não tem taxa de parcelamento.';
  return null;
}

/**
 * Simula uma opção de parcelamento.
 * @param repassar true = o cliente paga as taxas e a agência recebe o valor cheio;
 *                 false = o cliente paga o valor da venda e a agência absorve as taxas.
 */
export function simularParcela(valor: string, parcelas: number, repassar: boolean, tabela: TabelaTaxas = TAXAS_MERCADO_PAGO_PADRAO): OpcaoParcelamento {
  if (!Number.isInteger(parcelas) || parcelas < 1 || parcelas > tabela.parcelamentoBp.length) {
    throw new Error(`Número de parcelas inválido: ${parcelas}`);
  }
  const valorCents = toCents(valor);
  if (valorCents <= 0) throw new Error('O valor precisa ser maior que zero.');
  const parcBp = tabela.parcelamentoBp[parcelas - 1] as number;
  const totalBp = tabela.taxaVendaBp + parcBp;

  // Repassando: o cliente paga P tal que P − taxas(P) = valor  →  P = valor / (1 − taxa).
  // A parcela é arredondada para cima ao centavo, para a agência nunca receber menos.
  let clienteCents: number;
  let parcelaCents: number;
  if (repassar) {
    const bruto = (valorCents * 10_000) / (10_000 - totalBp);
    parcelaCents = Math.ceil(bruto / parcelas - 1e-9);
    clienteCents = parcelaCents * parcelas;
  } else {
    clienteCents = valorCents;
    parcelaCents = divRound(valorCents, parcelas);
  }
  const descVenda = divRound(clienteCents * tabela.taxaVendaBp, 10_000);
  const descParc = divRound(clienteCents * parcBp, 10_000);

  return {
    parcelas,
    taxaTotalBp: totalBp,
    taxaParcelamentoBp: parcBp,
    clientePaga: fromCents(clienteCents),
    valorParcela: fromCents(parcelaCents),
    voceRecebe: fromCents(clienteCents - descVenda - descParc),
    descontoTaxaVenda: fromCents(descVenda),
    descontoParcelamento: fromCents(descParc),
  };
}

/** Todas as opções de 1x até o máximo da tabela. */
export function simularTodas(valor: string, repassar: boolean, tabela: TabelaTaxas = TAXAS_MERCADO_PAGO_PADRAO): OpcaoParcelamento[] {
  return tabela.parcelamentoBp.map((_, i) => simularParcela(valor, i + 1, repassar, tabela));
}

/** 647 -> "6,47%" */
export function formatBp(bp: number): string {
  return `${Math.floor(bp / 100)},${String(bp % 100).padStart(2, '0')}%`;
}
